import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { ApiAssetType, ApiOrganization } from '@agunity/api-v4';
import { Box } from '@mui/material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid';

import OrgOtherHeader from '../OrgOtherHeader';
import AgTable from '../../../stories/AgTable';
import AgNoPermission from '../../../components/AgNoPermission';
import AgLoadingCard from '../../../stories/AgLoadingCard';
import AgFilterSearch, { FILTER_TYPE, FilterSearchProps, FilterSearchRef } from '../../../components/AgFilterSearch';

import { useToast } from '../../../redux/hooks/toast';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { selectFilteredUsers, selectUnits } from '../../../redux/slices/global';
import { ASSET_TYPES_READ_PERMISSION, ASSET_TYPES_WRITE_PERMISSION } from '../../../Constants';
import { getOrganizationItem, getOrgImage, getAssetTypesList, getAssetTypesAssociatedIds, postAssetTypesAssociatedIds } from './redux';
import * as S from './styled';

const OrgAssetTypes = () => {
  const apiRef = useGridApiRef();
  const filterSearchRef = useRef<FilterSearchRef| null>(null);
  const messages = useIntl().formatMessage;
  const { toast } = useToast();
  const orgId = useParams().orgId;
  const dispatch = useAppDispatch();
  const units = useAppSelector(selectUnits);

  const users = useAppSelector(selectFilteredUsers);
  const permissions = (users?.[0]?.permissions) || [];
  const canWrite = permissions.includes(ASSET_TYPES_WRITE_PERMISSION);
  const canRead = permissions.includes(ASSET_TYPES_READ_PERMISSION);
  const licenseOrgId = users?.[0]?.orgId;

  const [orgData, setOrgData] = useState<ApiOrganization>();
  const [assetTypes, setAssetTypes] = useState<ApiAssetType[]>([]);
  const [imageURL, setImageURL] = useState<string | undefined>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [origSelectedIds, setOrigSelectedIds] = useState<number[]>([]);

  const handleGetOrg = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await dispatch(getOrganizationItem(Number(orgId)));
      const data = response.data;
      if (data?.imageId) {
        const imageResponse = await dispatch(getOrgImage(data?.imageId!));
        const image = imageResponse?.data?.includes('data:image') 
          ? imageResponse.data 
          : 'data:image/jpeg;base64,' + imageResponse.data;
        setImageURL(image);
      }

      setOrgData(data);
    } catch (error) {
      console.error('Failed to fetch organization: ', error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, orgId]);

  // Fetch asset types, their associated IDs, and default yield units.
  const fetchAssetTypes = useCallback(async (showLoader = true) => {
    if (!orgId) {
      console.error('Invalid Organization ID!');
      return;
    }

    if (showLoader) setIsLoading(true);

    try {
      const [{ data: assets }, { data: activeIds }, { data: licenseActiveIds }] = await Promise.all([
        dispatch(getAssetTypesList()),
        dispatch(getAssetTypesAssociatedIds(Number(orgId))),
        dispatch(getAssetTypesAssociatedIds(Number(licenseOrgId))),
      ]);

      const assetTypesLicenseLevel = (assets || []).filter(({ id }) => (licenseActiveIds || []).includes(id!));
      setSelectedIds(activeIds);
      setOrigSelectedIds(activeIds);
      setAssetTypes(assetTypesLicenseLevel);
    } catch (error) {
      console.error('Failed to fetch asset types:', error);
    } finally {
      if (showLoader) setIsLoading(false);
    }
  }, [dispatch, orgId, licenseOrgId]);

  // Filter and/or search the asset types based on selected filters and search text.
  const handleFilterSearch = useCallback((values: FilterSearchProps) => {
    const { isTouched, type, search } = values;

    if (!isTouched || !assetTypes.length) return;

    let filteredList = [...assetTypes];

    if (type !== FILTER_TYPE.All) {
      const selectedOnly = type === FILTER_TYPE.Selected;
      filteredList = filteredList.filter(({ id }) => selectedOnly === selectedIds.includes(id!));
    }

    if (search) {
      const searchTerm = search.trim().toLowerCase();
      const fieldsToSearch = ['name', 'id'];
    
      filteredList = filteredList.filter((item: Record<string, any>) => {
        return fieldsToSearch
          .some((field) => String(item[field] || '')
            .toLowerCase()
            .includes(searchTerm)
          );
      });
    }

    // Update the datagrid rows instead of rows state
    if (apiRef?.current) {
      apiRef.current.setRows?.(filteredList);
    }
  }, [apiRef, assetTypes, selectedIds]);

    // Determine if changes have been made (used for disabling SAVE button)
    const idsNotEqual = useMemo(() => {
      const sortedSelected = [...selectedIds].sort((a, b) => a - b);
      const sortedOriginal = [...origSelectedIds].sort((a, b) => a - b);
      const selectedIdsChanged = JSON.stringify(sortedSelected) !== JSON.stringify(sortedOriginal);
  
      return selectedIdsChanged;
    }, [origSelectedIds, selectedIds]);

  // Save the currently selected asset type IDs.
  const handleSave = useCallback(async () => {
    if (!orgId) {
      toast('error', messages({ id: 'ASSET_TYPES_ERROR_INVALID_ORG_ID' }), { temporary: true });
      return;
    }

    try {
      await Promise.all([
        dispatch(postAssetTypesAssociatedIds(Number(orgId), selectedIds)),
      ]);

      toast('success', messages({ id: 'ASSET_TYPES_SAVED_SUCCESS' }), { temporary: true });

      // Re-fetch data without loader
      await fetchAssetTypes(false);
      
      // Clear filters
      if (filterSearchRef.current) {
        filterSearchRef.current.onClear();
      }
    } catch (error) {
      console.error('Failed to save selected asset types:', error);
    }
  }, [dispatch, fetchAssetTypes, messages, orgId, selectedIds, toast]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'id',
        headerName: messages({ id: 'ASSET_TYPES_ID' }),
      },
      {
        field: 'name',
        headerName: messages({ id: 'ASSET_TYPES_NAME' }),
        minWidth: 264,
      },
      {
        field: 'description',
        headerName: messages({ id: 'DESCRIPTION' }),
        minWidth: 264,
        flex: 1
      },
      {
        field: 'unitId',
        headerName: messages({ id: 'ASSET_TYPES_UNIT' }),
        valueGetter: ({ value }) => units.find(({ id }) => id === value)?.name,
        minWidth: 150,
      },
      {
        field: 'baseType',
        headerName: messages({ id: 'TYPE' }),
        valueGetter: ({ value }) => messages({ id: `ASSET_BASE_TYPE_${value}` }),
        minWidth: 150,
      },
    ],
    [messages, units]
  );

  useEffect(() => {
    const debounce = setTimeout(() => {
      handleGetOrg();
      fetchAssetTypes();
    });

    return () => clearTimeout(debounce);
  }, [handleGetOrg, fetchAssetTypes]);

  if (isLoading) return <AgLoadingCard text={messages({ id: 'LOADING' })} />;

  if (!canRead && !canWrite) return <AgNoPermission />;

  return (
    <S.Main>
      <OrgOtherHeader 
        orgId={orgId!} 
        orgData={orgData!} 
        imageURL={imageURL!}
        label={messages({ id: 'ASSET_TYPES' })}
        description={messages({ id: 'ASSET_TYPES_DESCRIPTION' })}
      />

      <Box sx={{ mt: 3, width: 'calc(100vw - 290px)', '.MuiDataGrid-main': { height: 'calc(100vh - 365px) !important' } }}>
        <AgTable
          showToolbar
          type='generic'
          apiRef={apiRef}
          columns={columns}
          initialRows={assetTypes}
          addNewbuttonName='SAVE'
          tableLoading={isLoading}
          showAddNewButtonIcon={false}
          showTableMenu={false}
          selectedIds={selectedIds}
          addNewButtonDisabled={!idsNotEqual || !canWrite}
          checkboxDisabled={!canWrite}
          disabledHighlight
          keepNonExistentRowsSelected
          setSort={() => {}}
          handleAddNewButton={handleSave}
          setSelectedIDs={setSelectedIds}
          tableToolBarLeftElementFilter={
            <AgFilterSearch
              ref={filterSearchRef} 
              setData={handleFilterSearch} 
              placeholder={messages({ id: 'ASSET_TYPES_SEARCH_PLACEHOLDER' })}
            />
          }
          noRowsOverlayContent={messages({ id: 'GENERIC_NO_TABLE_ROWS' })}
        />
      </Box>

    </S.Main>
  );
};

export default OrgAssetTypes;